import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../Header';
import Sidebar from '../Sidebar';
import teleChatApi from '../../api/telechat.api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import ConnectionStatus from './ConnectionStatus';
import { Card, StatusPill, EmptyState } from '../../ui';
import { morning_img_03 } from '../imagepath';

// Status → design-system tone + Bulgarian label (shared with the doctor screens)
const STATUS = {
  PENDING_APPROVAL: { tone: 'warning', label: 'Чака одобрение' },
  ACTIVE:           { tone: 'success', label: 'Активна' },
  CLOSED:           { tone: 'neutral', label: 'Приключена' },
  DECLINED:         { tone: 'danger',  label: 'Отказана' },
};

// Patient lobby ("Моите консултации") styling — implements the design with
// varvara-ui tokens + primitives. Desktop table / mobile cards toggle at 768px.
const LOBBY_CSS = `
  .vc-page {
    font-family: var(--vc-font-sans); color: var(--vc-color-text);
    padding: var(--vc-space-5); width: 100%;
    max-width: var(--vc-container); margin: 0 auto;
    display: flex; flex-direction: column; gap: var(--vc-space-4);
  }
  /* Desktop only: give the greeting illustration room to overflow upward without
     being hidden behind the fixed header (dashboards get this from their breadcrumb
     row). Mobile doesn't have the overlap, so no extra top gap there. */
  @media (min-width: 769px) {
    .vc-page .good-morning-blk { margin-top: var(--vc-space-12); }
  }

  /* ---- page header row: title + actions ---- */
  .lb-head { display: flex; align-items: center; gap: var(--vc-space-3); }
  .lb-titlewrap { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: var(--vc-space-3); }
  .lb-titleicon {
    flex: none; width: 42px; height: 42px; border-radius: var(--vc-radius-lg);
    background: var(--vc-color-primary-soft); color: var(--vc-color-primary);
    display: grid; place-items: center;
  }
  .lb-title { margin: 0; font-size: var(--vc-text-2xl); font-weight: var(--vc-weight-semibold); line-height: var(--vc-leading-tight); }
  .lb-subtitle { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); margin-top: 2px; }
  .lb-actions { display: flex; align-items: center; gap: var(--vc-space-2); flex: none; }

  .lb-btn {
    appearance: none; cursor: pointer; text-decoration: none;
    display: inline-flex; align-items: center; gap: 7px;
    font-family: inherit; font-size: var(--vc-text-base); font-weight: var(--vc-weight-semibold);
    border-radius: var(--vc-radius-md); padding: var(--vc-space-2) var(--vc-space-4);
    border: 1px solid transparent; transition: all .12s ease; white-space: nowrap;
  }
  .lb-btn-primary { background: var(--vc-color-primary); color: #fff; box-shadow: var(--vc-shadow-sm); }
  .lb-btn-primary:hover { filter: brightness(1.08); color: #fff; }
  .lb-icon-btn {
    appearance: none; cursor: pointer; flex: none;
    display: inline-grid; place-items: center; width: 42px; height: 42px;
    color: var(--vc-color-text-muted); background: var(--vc-color-surface);
    border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md);
    transition: color .12s ease, border-color .12s ease;
  }
  .lb-icon-btn:hover { color: var(--vc-color-primary); border-color: var(--vc-color-primary); }
  .lb-icon-btn:active svg { transition: transform .5s ease; transform: rotate(180deg); }

  /* ---- desktop table ---- */
  .lb-table { width: 100%; border-collapse: collapse; }
  .lb-table thead th {
    text-align: left; font-size: var(--vc-text-xs); font-weight: var(--vc-weight-semibold);
    text-transform: uppercase; letter-spacing: .05em; color: var(--vc-color-text-subtle);
    padding: 0 var(--vc-space-3) var(--vc-space-2); border-bottom: 1px solid var(--vc-color-border);
  }
  .lb-row { cursor: pointer; transition: background .12s ease; }
  .lb-row:hover { background: var(--vc-color-primary-soft); }
  .lb-row > td { padding: var(--vc-space-3); border-bottom: 1px solid var(--vc-color-border-soft); vertical-align: middle; }
  .lb-row:last-child > td { border-bottom: none; }

  .lb-doc-cell { display: flex; align-items: center; gap: var(--vc-space-3); min-width: 0; }
  .lb-avatar {
    flex: none; width: 40px; height: 40px; border-radius: var(--vc-radius-pill);
    background: var(--vc-color-primary); color: #fff;
    display: grid; place-items: center; font-weight: var(--vc-weight-bold); font-size: var(--vc-text-md);
  }
  .lb-doc-name { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-primary); line-height: var(--vc-leading-tight); }
  .lb-row:hover .lb-doc-name { text-decoration: underline; }
  .lb-spec { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); }
  .lb-time { font-family: var(--vc-font-mono); font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; }
  .lb-last { display: flex; align-items: center; gap: var(--vc-space-2); min-width: 0; max-width: 320px; }
  .lb-last-text { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .lb-row.unread .lb-last-text { color: var(--vc-color-text); font-weight: var(--vc-weight-medium); }
  .unread-badge {
    flex: none; display: inline-grid; place-items: center;
    min-width: 20px; height: 20px; padding: 0 6px; border-radius: var(--vc-radius-pill);
    background: var(--vc-color-primary); color: #fff;
    font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); font-weight: var(--vc-weight-bold); line-height: 1;
  }
  .lb-chev { text-align: right; color: var(--vc-color-text-subtle); }

  /* ---- mobile cards ---- */
  .lb-cards { display: flex; flex-direction: column; gap: var(--vc-space-3); }
  .lb-card { display: flex; flex-direction: column; gap: var(--vc-space-3); }
  .lb-card-top { display: flex; align-items: center; gap: var(--vc-space-3); }
  .lb-card .lb-avatar { width: 46px; height: 46px; }
  .lb-card-id { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .lb-card-name { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-primary); line-height: var(--vc-leading-tight); overflow-wrap: anywhere; }
  .lb-card-meta { display: flex; align-items: center; gap: var(--vc-space-2) var(--vc-space-3); flex-wrap: wrap; }
  .lb-card-msg {
    display: flex; align-items: center; gap: var(--vc-space-2);
    padding: var(--vc-space-2) var(--vc-space-3);
    background: var(--vc-color-surface-muted); border: 1px solid var(--vc-color-border-soft);
    border-radius: var(--vc-radius-md);
  }
  .lb-card-msg .txt { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; flex: 1 1 auto; }
  .lb-card.unread .lb-card-msg .txt { color: var(--vc-color-text); font-weight: var(--vc-weight-medium); }
  a.lb-linkwrap { text-decoration: none; color: inherit; display: block; }

  /* ---- loading skeleton ---- */
  .sk { background: linear-gradient(90deg, var(--vc-color-border-soft) 25%, var(--vc-color-surface-muted) 50%, var(--vc-color-border-soft) 75%); background-size: 200% 100%; animation: sk 1.3s ease infinite; border-radius: var(--vc-radius-sm); }
  @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .sk-row { display: flex; align-items: center; gap: var(--vc-space-3); padding: var(--vc-space-3); border-bottom: 1px solid var(--vc-color-border-soft); }
  .sk-row:last-child { border-bottom: none; }
  .sk-avatar { flex: none; width: 40px; height: 40px; border-radius: var(--vc-radius-pill); }
  .sk-lines { flex: 1 1 auto; display: flex; flex-direction: column; gap: 7px; }

  /* ---- responsive: table on desktop, cards on mobile ---- */
  .lb-desktop { display: block; }
  .lb-mobile { display: none; }
  @media (max-width: 768px) {
    .lb-desktop { display: none; }
    .lb-mobile { display: block; }
    .lb-title { font-size: var(--vc-text-xl); }
    .lb-head { flex-wrap: wrap; }
    .lb-actions { width: 100%; }
    .lb-actions .lb-btn-primary { flex: 1 1 auto; justify-content: center; }
  }
`;

// --- helpers ---
// Strip a leading "Д-Р " title, take the first real name's initial
const docInitial = (name) => {
  if (!name) return 'Д';
  const clean = name.replace(/^Д-?Р\s+/i, '').trim();
  return (clean[0] || name[0] || '?').toUpperCase();
};

const PatientChatList = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { subscribeToLobbyUpdates } = useWebSocket();

  useEffect(() => {
    loadChats();
  }, []);

  // Refetch chats when page becomes visible (after phone unlock or app switch)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) loadChats();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Subscribe to WebSocket lobby updates
  useEffect(() => {
    const unsubscribe = subscribeToLobbyUpdates((event) => {
      if (event.type === 'LOBBY_UPDATE') {
        setChats((prev) => {
          const index = prev.findIndex((c) => c.chatId === event.chatId);
          if (index === -1) return [event.payload, ...prev];
          const updated = [...prev];
          updated[index] = event.payload;
          return updated;
        });
      } else if (event.type === 'CHAT_CLOSED') {
        setChats((prev) => {
          const index = prev.findIndex((c) => c.chatId === event.chatId);
          if (index === -1) return prev;
          const updated = [...prev];
          updated[index] = { ...updated[index], status: 'CLOSED' };
          return updated;
        });
      } else if (event.type === 'CHAT_APPROVED') {
        setChats((prev) => {
          const index = prev.findIndex((c) => c.chatId === event.chatId);
          if (index === -1) return prev;
          const updated = [...prev];
          updated[index] = { ...updated[index], status: 'ACTIVE', paymentStatus: 'CAPTURED' };
          return updated;
        });
      } else if (event.type === 'CHAT_DECLINED') {
        setChats((prev) => {
          const index = prev.findIndex((c) => c.chatId === event.chatId);
          if (index === -1) return prev;
          const updated = [...prev];
          updated[index] = { ...updated[index], status: 'DECLINED', paymentStatus: 'CANCELLED' };
          return updated;
        });
      }
    });
    return () => unsubscribe();
  }, [subscribeToLobbyUpdates]);

  const loadChats = async () => {
    try {
      setLoading(true);
      const data = await teleChatApi.getPatientChats();
      setChats(data);
    } catch (error) {
      console.error('Error loading chats:', error);
      message.error('Грешка при зареждане на консултациите');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Backend sends LocalDateTime without offset — treat as UTC, display in Sofia
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    const datePart = date.toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Sofia' });
    const timePart = date.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Sofia' });
    return `${datePart} ${timePart}`;
  };

  const openChat = (chat) => navigate(`/telechat/${chat.chatId}`);
  const startNew = () => navigate('/telechat/new');

  const subtitle = chats.length > 0
    ? `${chats.length} ${chats.length === 1 ? 'консултация' : 'консултации'}`
    : 'Онлайн консултации с лекари';

  const renderTableRow = (c) => {
    const s = STATUS[c.status] || { tone: 'neutral', label: c.status };
    return (
      <tr key={c.chatId} className={'lb-row' + (c.unreadCount > 0 ? ' unread' : '')} onClick={() => openChat(c)}>
        <td>
          <div className="lb-doc-cell">
            <span className="lb-avatar">{docInitial(c.doctor?.fullName)}</span>
            <span className="lb-doc-name">{c.doctor?.fullName || 'Неизвестен лекар'}</span>
          </div>
        </td>
        <td><span className="lb-spec">{c.doctor?.speciality || '-'}</span></td>
        <td><StatusPill tone={s.tone} size="sm">{s.label}</StatusPill></td>
        <td><span className="lb-time">{formatDate(c.createdAt)}</span></td>
        <td>
          <div className="lb-last">
            <span className="lb-last-text">{c.lastMessage || 'Няма съобщения'}</span>
            {c.unreadCount > 0 && <span className="unread-badge">{c.unreadCount}</span>}
          </div>
        </td>
        <td className="lb-chev"><FeatherIcon icon="chevron-right" size={18} /></td>
      </tr>
    );
  };

  const renderCard = (c) => {
    const s = STATUS[c.status] || { tone: 'neutral', label: c.status };
    return (
      <a
        key={c.chatId}
        className="lb-linkwrap"
        href={`/telechat/${c.chatId}`}
        onClick={(e) => { e.preventDefault(); openChat(c); }}
      >
        <Card flat padding="compact">
          <div className={'lb-card' + (c.unreadCount > 0 ? ' unread' : '')}>
            <div className="lb-card-top">
              <span className="lb-avatar">{docInitial(c.doctor?.fullName)}</span>
              <div className="lb-card-id">
                <span className="lb-card-name">{c.doctor?.fullName || 'Неизвестен лекар'}</span>
                <span className="lb-spec">{c.doctor?.speciality || '-'}</span>
                <div className="lb-card-meta">
                  <StatusPill tone={s.tone} size="sm">{s.label}</StatusPill>
                  <span className="lb-time">{formatDate(c.createdAt)}</span>
                </div>
              </div>
            </div>
            {c.lastMessage && (
              <div className="lb-card-msg">
                <span className="txt">{c.lastMessage}</span>
                {c.unreadCount > 0 && <span className="unread-badge">{c.unreadCount}</span>}
              </div>
            )}
          </div>
        </Card>
      </a>
    );
  };

  const renderSkeleton = (rows) => (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="sk-row" key={i}>
          <span className="sk sk-avatar" />
          <span className="sk-lines">
            <span className="sk" style={{ height: 12, width: '45%' }} />
            <span className="sk" style={{ height: 10, width: '70%' }} />
          </span>
          <span className="sk" style={{ height: 22, width: 84, borderRadius: 'var(--vc-radius-pill)' }} />
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return <Card padding="comfortable">{renderSkeleton(5)}</Card>;
    }
    if (chats.length === 0) {
      return (
        <Card padding="comfortable">
          <EmptyState
            icon={<FeatherIcon icon="message-circle" size={28} />}
            title="Нямате консултации"
            description="Нямате консултации. Натиснете «Нова консултация» за да започнете."
            action={<button className="lb-btn lb-btn-primary" onClick={startNew}><FeatherIcon icon="plus" size={16} /> Нова консултация</button>}
          />
        </Card>
      );
    }
    return (
      <>
        <div className="lb-desktop">
          <Card padding="flush">
            <div style={{ padding: 'var(--vc-space-2)' }}>
              <table className="lb-table">
                <thead>
                  <tr>
                    <th>Лекар</th>
                    <th>Специалност</th>
                    <th>Статус</th>
                    <th>Дата</th>
                    <th>Последно съобщение</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{chats.map(renderTableRow)}</tbody>
              </table>
            </div>
          </Card>
        </div>
        <div className="lb-mobile">
          <div className="lb-cards">{chats.map(renderCard)}</div>
        </div>
      </>
    );
  };

  return (
    <div>
      <Header />
      <Sidebar activeClassName="telechat" />
      <style>{LOBBY_CSS}</style>

      <div className="page-wrapper">
        <div className="content" style={{ padding: 0 }}>
          <div className="vc-page">
            {/* Welcome hero */}
            <div className="good-morning-blk">
              <div className="row">
                <div className="col-md-6">
                  <div className="morning-user">
                    <h2>Здравейте</h2>
                    <p>Свържете се с Вашия лекар онлайн — бързо, лесно и защитено.</p>
                  </div>
                </div>
                <div className="col-md-6 position-blk">
                  <div className="morning-img">
                    <img src={morning_img_03} alt="" />
                  </div>
                </div>
              </div>
            </div>

            {/* Header row */}
            <div className="lb-head">
              <div className="lb-titlewrap">
                <span className="lb-titleicon"><FeatherIcon icon="message-square" size={22} /></span>
                <div style={{ minWidth: 0 }}>
                  <h1 className="lb-title">Моите консултации</h1>
                  <div className="lb-subtitle">{subtitle}</div>
                </div>
              </div>
              <div className="lb-actions">
                <button className="lb-btn lb-btn-primary" onClick={startNew}>
                  <FeatherIcon icon="plus" size={17} /> Нова консултация
                </button>
                <button className="lb-icon-btn" onClick={loadChats} title="Опресни" aria-label="Опресни">
                  <FeatherIcon icon="refresh-cw" size={17} />
                </button>
              </div>
            </div>

            <ConnectionStatus />

            {renderBody()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientChatList;
